import { useEffect, useMemo, useRef, useState } from "react";
import { api, cachedGet } from "@/lib/apiClient";
import { Clock, CheckCircle2, CalendarPlus, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Countdown to the consultant's weekly reporting cutoff
 * (= club meeting datetime - 60 minutes).
 *
 * Visible to consultors and coaches. Updates every 30s.
 *
 * States:
 *  - upcoming    → edition has not started
 *  - locked      → cutoff already passed for current week
 *  - ended       → edition is over
 *  - active      → live countdown to cutoff
 */
export default function MeetingCountdown() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [club, setClub] = useState(null);
  const [meetingIso, setMeetingIso] = useState(null);
  const [nextIso, setNextIso] = useState(null);
  const [now, setNow] = useState(Date.now());
  const tickRef = useRef(null);

  useEffect(() => {
    let alive = true;
    cachedGet("/settings").then((r) => { if (alive) setSettings(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // Resolve user's club (coach has user.club; consultant needs /captain/me to get player club)
  useEffect(() => {
    if (!user) return;
    if (user.role === "coach" && user.club) {
      setClub(user.club);
      return;
    }
    if (user.role !== "consultant" && user.role !== "admin") return;
    let alive = true;
    api.get("/captain/me").then((r) => { if (alive) setClub(r.data?.club || null); }).catch(() => {});
    return () => { alive = false; };
  }, [user]);

  // Fetch this week's and next week's meeting datetimes for the resolved club.
  // Polls every 2 minutes so admin changes to the schedule propagate without reload.
  useEffect(() => {
    if (!settings?.current_week_computed || !club) return;
    let alive = true;
    const fetchOnce = () => api.get("/settings/club-meetings").then((r) => {
      if (!alive) return;
      const wk = settings.current_week_computed;
      const sched = r.data?.[club]?.schedule || {};
      setMeetingIso(sched[wk] || null);
      setNextIso(sched[wk + 1] || null);
    }).catch(() => {});
    fetchOnce();
    const refresh = setInterval(fetchOnce, 120_000);
    return () => { alive = false; clearInterval(refresh); };
  }, [settings, club]);

  // Tick every 30s
  useEffect(() => {
    tickRef.current = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(tickRef.current);
  }, []);

  const { state, target, label, isFirst, week } = useMemo(() => {
    if (!settings) return { state: "loading" };
    if (settings.status === "ended") return { state: "ended" };
    if (settings.status === "upcoming") {
      // If a meeting is already scheduled for week 1, surface it prominently — players see
      // when the first meeting will happen even before the league officially starts.
      if (meetingIso) {
        return {
          state: "upcoming_with_meeting",
          target: meetingIso,
          label: "Primeira reunião · Semana 1",
          isFirst: true,
        };
      }
      return { state: "upcoming", target: settings.start_date_iso };
    }
    if (!meetingIso) return { state: "no_meeting" };
    const meetingMs = new Date(meetingIso).getTime();
    if (now > meetingMs) {
      // Reunião desta semana já passou — conta já para a da semana seguinte.
      if (nextIso && new Date(nextIso).getTime() > now) {
        return {
          state: "active",
          target: nextIso,
          label: `Próxima reunião · Semana ${settings.current_week_computed + 1}`,
          week: settings.current_week_computed + 1,
        };
      }
      return { state: "after_meeting", target: meetingIso };
    }
    return {
      state: "active",
      target: meetingIso,
      label: `Próxima reunião · Semana ${settings.current_week_computed}`,
      week: settings.current_week_computed,
    };
  }, [settings, meetingIso, nextIso, now]);

  // Show for any authenticated user with a resolvable club (admins who are also linked
  // to a player record will see it too).
  if (!user) return null;
  if (state === "loading" || state === "no_meeting") return null;

  if (state === "ended") {
    return (
      <Card icon={CheckCircle2} color="#737373" title="Edição terminada" subtitle="O ciclo da liga já fechou." testid="meeting-countdown-ended" />
    );
  }

  if (state === "upcoming") {
    const d = settings.start_date ? new Date(settings.start_date + "T12:00:00") : null;
    const pretty = d ? d.toLocaleDateString("pt-PT", { weekday: "long", day: "2-digit", month: "long" }) : "—";
    const phase = settings.prep_weeks > 0
      ? `Arranca com ${settings.prep_weeks} ${settings.prep_weeks === 1 ? "semana" : "semanas"} de preparação — a coopetição começa na semana ${settings.competition_start_week}.`
      : "A edição arranca nesta data — fica atento.";
    return (
      <Card icon={Clock} color="#3B82F6" title={pretty}
        subtitle={phase} testid="meeting-countdown-upcoming" />
    );
  }

  if (state === "after_meeting") {
    return (
      <Card icon={CheckCircle2} color="#737373" title="Reunião desta semana já passou"
        subtitle={`Reunião ${fmtShortDateTime(target)}. Continua a reportar — a janela só fecha domingo às 23:59.`}
        testid="meeting-countdown-after" />
    );
  }

  // ACTIVE (or UPCOMING_WITH_MEETING) — live countdown to the meeting itself.
  const remainingSec = Math.max(0, Math.floor((new Date(target).getTime() - now) / 1000));
  const urgent = remainingSec < 24 * 3600 && state === "active";
  const color = urgent ? "#E50000" : (isFirst ? "#3B82F6" : "#fff");
  const currentWeek = isFirst ? 1 : (week || settings.current_week_computed);
  const subtitle = isFirst
    ? "A edição ainda não começou, mas a tua primeira reunião já tem data. Adiciona já ao calendário."
    : (urgent ? "Reunião em breve — chega a horas para ganhares pontualidade." : "Esta data atualiza automaticamente se o admin alterar o horário.");
  return (
    <div className="kw-card !overflow-visible p-5 flex items-center justify-between gap-4 flex-wrap" data-testid={isFirst ? "meeting-countdown-first" : "meeting-countdown-active"}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <Clock className="w-6 h-6 flex-shrink-0" style={{ color }} />
        <div className="min-w-0">
          <div className="kw-overline" style={{ color }}>{label}</div>
          <div className="font-display text-xl md:text-2xl font-black tracking-tight mt-0.5 capitalize truncate" data-testid="meeting-countdown-datetime">
            {fmtDateTime(target)}
          </div>
          <div className="text-[11px] text-[#737373] mt-1">{subtitle}</div>
          <CalendarSyncButton club={club} week={currentWeek} meetingIso={target} />
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="font-display text-3xl md:text-4xl font-black tracking-tight" style={{ color }} data-testid="meeting-countdown-value">
          {fmtCountdown(remainingSec)}
        </div>
        <div className="kw-overline">{isFirst ? "para a 1.ª" : "restantes"}</div>
      </div>
    </div>
  );
}

function CalendarSyncButton({ club, week, meetingIso }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  if (!club || !week) return null;
  const base = process.env.REACT_APP_BACKEND_URL || "";

  // ICS download (Apple Calendar / Thunderbird / season import).
  const downloadIcs = async (kind) => {
    const path = kind === "season"
      ? `/calendar/club/${encodeURIComponent(club)}/season.ics`
      : `/calendar/club/${encodeURIComponent(club)}/week/${week}.ics`;
    try {
      const res = await api.get(path, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/calendar;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "season"
        ? `kw-lead-${club.toLowerCase()}-todas-reunioes.ics`
        : `kw-lead-${club.toLowerCase()}-semana-${week}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      window.open(`${base}/api${path}`, "_blank");
    }
    setOpen(false);
  };

  // Build a single-event deep link for Google/Outlook for the CURRENT week's meeting.
  // These open the provider's web UI directly — no .ics association required.
  const buildEvent = () => {
    if (!meetingIso) return null;
    const start = new Date(meetingIso);
    if (isNaN(start.getTime())) return null;
    const end = new Date(start.getTime() + 90 * 60_000); // 90-minute default duration
    const fmt = (d) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
    return {
      title: `KW Lead · Reunião ${club} · Semana ${week}`,
      description: `Reunião semanal do clube ${club} (KW Lead Growth League).\nAjusta o evento conforme o horário oficial da edição.`,
      location: "Online · KW Lead Growth League",
      startUtc: fmt(start),
      endUtc: fmt(end),
      startLocalOutlook: start.toISOString(),
      endLocalOutlook: end.toISOString(),
    };
  };

  const openGoogle = () => {
    const ev = buildEvent();
    if (!ev) return downloadIcs("week");
    const url = new URL("https://calendar.google.com/calendar/render");
    url.searchParams.set("action", "TEMPLATE");
    url.searchParams.set("text", ev.title);
    url.searchParams.set("dates", `${ev.startUtc}/${ev.endUtc}`);
    url.searchParams.set("details", ev.description);
    url.searchParams.set("location", ev.location);
    window.open(url.toString(), "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  const openOutlook = () => {
    const ev = buildEvent();
    if (!ev) return downloadIcs("week");
    const url = new URL("https://outlook.live.com/calendar/0/deeplink/compose");
    url.searchParams.set("path", "/calendar/action/compose");
    url.searchParams.set("rru", "addevent");
    url.searchParams.set("subject", ev.title);
    url.searchParams.set("startdt", ev.startLocalOutlook);
    url.searchParams.set("enddt", ev.endLocalOutlook);
    url.searchParams.set("body", ev.description);
    url.searchParams.set("location", ev.location);
    window.open(url.toString(), "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  return (
    <div className="relative inline-block mt-3" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#E50000]/40 hover:border-[#E50000] hover:bg-[#E50000]/10 text-xs uppercase tracking-widest font-bold text-[#E50000] transition-colors"
        data-testid="calendar-sync-btn"
      >
        <CalendarPlus className="w-3.5 h-3.5" />
        Adicionar ao Calendário
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 w-72 bg-[#0A0A0A] border border-[#222] shadow-xl z-20" data-testid="calendar-sync-menu">
          <button onClick={openGoogle}
            className="w-full text-left px-4 py-3 hover:bg-[#1A1A1A] border-b border-[#222]"
            data-testid="calendar-sync-google"
            disabled={!meetingIso}>
            <div className="text-xs font-bold uppercase tracking-widest text-white">Google Calendar</div>
            <div className="text-[10px] text-[#737373] mt-0.5">Abre direto no Google · esta semana</div>
          </button>
          <button onClick={openOutlook}
            className="w-full text-left px-4 py-3 hover:bg-[#1A1A1A] border-b border-[#222]"
            data-testid="calendar-sync-outlook"
            disabled={!meetingIso}>
            <div className="text-xs font-bold uppercase tracking-widest text-white">Outlook</div>
            <div className="text-[10px] text-[#737373] mt-0.5">Abre direto no Outlook · esta semana</div>
          </button>
          <button onClick={() => downloadIcs("week")}
            className="w-full text-left px-4 py-3 hover:bg-[#1A1A1A] border-b border-[#222]"
            data-testid="calendar-sync-week">
            <div className="text-xs font-bold uppercase tracking-widest text-white">Apple Calendar / Outros</div>
            <div className="text-[10px] text-[#737373] mt-0.5">Descarrega ficheiro .ics · esta semana</div>
          </button>
          <button onClick={() => downloadIcs("season")}
            className="w-full text-left px-4 py-3 hover:bg-[#1A1A1A]"
            data-testid="calendar-sync-season">
            <div className="text-xs font-bold uppercase tracking-widest text-white">Toda a temporada (.ics)</div>
            <div className="text-[10px] text-[#737373] mt-0.5">Todas as reuniões num só ficheiro</div>
          </button>
          <div className="px-4 py-2 bg-[#0A0A0A] border-t border-[#222]">
            <div className="text-[10px] text-[#737373]">Lembrete 30 min antes incluído quando importas o .ics.</div>
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ icon: Icon, color, title, subtitle, testid }) {
  return (
    <div className="kw-card p-5 flex items-start gap-3" data-testid={testid}>
      <Icon className="w-5 h-5 mt-1" style={{ color }} />
      <div>
        <div className="kw-overline" style={{ color }}>{title}</div>
        <div className="text-xs text-[#A3A3A3] mt-1">{subtitle}</div>
      </div>
    </div>
  );
}

function fmtCountdown(sec) {
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${String(m).padStart(2, "0")}m`;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  return `${m}m`;
}
function fmtDateTime(iso) {
  try { return new Date(iso).toLocaleString("pt-PT", { weekday: "long", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
}
function fmtShortDateTime(iso) {
  try { return new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
}
